import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  MapPin,
  Calendar,
  Users,
  Clock,
  Star,
  TrendingUp,
  Heart,
  ChevronRight,
  Volume2,
  VolumeX,
} from "lucide-react";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import { getAllExperiences, ExperienceSummary } from "../../redux/reducers/Experiences/ExperiencesSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { huarazLake69 } from "../../images/experiences";

const ExperiencesPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { experiences, loading } = useSelector((state: RootState) => state.experiences);
  const [activeCategory, setActiveCategory] = useState("All");
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);

  const categories = [
    "All",
    "Adventure",
    "Cultural",
    "Beach & Surf",
    "Food & Nightlife",
    "Sports",
    "Weekend Trips",
  ];

  useEffect(() => {
    dispatch(getAllExperiences());
  }, [dispatch]);

  const filteredExperiences =
    activeCategory === "All"
      ? experiences
      : experiences.filter((exp: ExperienceSummary) => exp.category === activeCategory);

  const featuredExperiences = experiences.slice(0, 6);

  if (loading) {
    return (
      <div className="nc-ExperiencesPage min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#f5694b] mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading experiences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="nc-ExperiencesPage">
      <BgGlassmorphism />

      {/* ========== HERO SECTION ========== */}
      <div className="relative h-[500px] lg:h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={huarazLake69}
            alt="Laguna 69 Huaraz Peru - Nomadroof Adventures"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 75%" }}
          />
        </div>

        <div className="container relative z-10 px-4 mx-auto h-full flex flex-col items-center justify-start pt-8 sm:pt-12 md:pt-16 lg:pt-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-black/40 backdrop-blur-md rounded-full border border-white/30 mb-4 sm:mb-6">
            <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
            <span className="text-xs sm:text-sm font-semibold text-white">
              For Our Exclusive Community
            </span>
          </div>

          <h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 px-4"
            style={{ textShadow: "2px 2px 8px rgba(0,0,0,0.8), 0 0 20px rgba(0,0,0,0.5)" }}
          >
            Live Peru to the Fullest
          </h1>

          <p
            className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl font-semibold text-white max-w-xs sm:max-w-md md:max-w-2xl lg:max-w-3xl mx-auto mb-6 sm:mb-8 leading-tight px-4"
            style={{ textShadow: "2px 2px 10px rgba(0,0,0,0.9), 0 0 30px rgba(0,0,0,0.7)" }}
          >
            The International Community for Exchange Students and Young Professionals
          </p>
        </div>
      </div>

{/* ========== VIDEO SECTION ========== */}
<div className="w-full py-16 lg:py-20">
  <div className="container px-4 mx-auto">
    <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

      {/* Phone frame - left on desktop, top on mobile */}
      <div className="flex-shrink-0 flex justify-center">
        <div
          className="relative rounded-[3rem] overflow-hidden shadow-2xl border-[6px] border-gray-900 dark:border-gray-700"
          style={{ width: "300px", height: "620px" }}
        >
          {/* Notch */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-900 dark:bg-gray-700 rounded-b-2xl z-10" />

          <video
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          >
            <source
              src="https://res.cloudinary.com/delmdmex1/video/upload/v1772812058/huaraz-nomadroof-experiences-header-hero_dkhyzi.mov"
              type="video/mp4"
            />
          </video>
        </div>
      </div>

      {/* Text content - right on desktop, bottom on mobile */}
      <div className="flex-1 text-center lg:text-left">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#f5694b]/10 rounded-full border border-[#f5694b]/30 mb-6">
          <TrendingUp className="w-4 h-4 text-[#f5694b]" />
          <span className="text-sm font-semibold text-[#f5694b]">
            For Our Exclusive Community
          </span>
        </div>

        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
          Experience Peru Like{" "}
          <span className="text-[#f5694b]">Never Before</span>
        </h2>

        <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-lg mx-auto lg:mx-0">
          Join our community of explorers and discover the beauty, culture, and adventure that Peru has to offer — curated exclusively for exchange students and young professionals.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#f5694b]/10 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-[#f5694b]" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Top Destinations</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Huaraz, Cusco, Lima & more</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#f5694b]/10 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-[#f5694b]" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Group Adventures</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Meet people from 50+ countries</p>
            </div>
          </div>
        </div>

        <Link
          to="/contact"
          className="inline-flex items-center gap-2 px-8 py-4 bg-[#f5694b] hover:bg-[#e2583d] text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
        >
          <span>Join the Community</span>
          <ChevronRight className="w-5 h-5" />
        </Link>
      </div>

    </div>
  </div>
</div>

      {/* ========== FEATURED ADVENTURES ========== */}
      <div className="container px-4 mx-auto py-16 lg:py-20">
        <div className="mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Featured Adventures
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Our most popular experiences - don't miss out!
          </p>
        </div>

        {featuredExperiences.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-4 lg:gap-6 mb-16">
            {featuredExperiences[0] && (
              <div className="md:col-span-3 lg:col-span-4">
                <FeaturedCardMedium experience={featuredExperiences[0]} />
              </div>
            )}
            {featuredExperiences[1] && (
              <div className="md:col-span-3 lg:col-span-8">
                <FeaturedCardWide experience={featuredExperiences[1]} />
              </div>
            )}
            {featuredExperiences[2] && (
              <div className="md:col-span-3 lg:col-span-5 md:row-span-2">
                <FeaturedCardLarge experience={featuredExperiences[2]} />
              </div>
            )}
            {featuredExperiences[3] && (
              <div className="md:col-span-3 lg:col-span-4">
                <FeaturedCardMedium experience={featuredExperiences[3]} />
              </div>
            )}
            {featuredExperiences[4] && (
              <div className="md:col-span-3 lg:col-span-3">
                <FeaturedCardSmall experience={featuredExperiences[4]} />
              </div>
            )}
            {featuredExperiences[5] && (
              <div className="md:col-span-3 lg:col-span-5">
                <FeaturedCardMedium experience={featuredExperiences[5]} />
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-16 text-gray-400">
            <p>No featured experiences yet. Check back soon!</p>
          </div>
        )}
      </div>

      {/* ========== CATEGORY FILTER & ALL EXPERIENCES ========== */}
      <div className="container px-4 mx-auto">
        <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide mb-8">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-3 rounded-full font-semibold whitespace-nowrap transition-all duration-300 ${
                activeCategory === category
                  ? "bg-[#f5694b] text-white shadow-lg scale-105"
                  : "bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 border border-gray-200 dark:border-neutral-700"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {filteredExperiences.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-16">
            {filteredExperiences.map((experience) => (
              <ExperienceCard key={experience.id} experience={experience} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-gray-400">
            <p>No experiences found in this category.</p>
          </div>
        )}
      </div>

      {/* ========== CTA SECTION ========== */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-neutral-800 dark:to-neutral-900 py-16 lg:py-20">
        <div className="container px-4 mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Ready for Your Next Adventure?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
            Join our community of explorers and experience the best of Peru with
            local guides and new friends.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#f5694b] hover:bg-[#e2583d] text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <span>Get in Touch</span>
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// ============================================
// FEATURED CARD COMPONENTS
// ============================================

interface FeaturedCardProps {
  experience: ExperienceSummary;
}

const FeaturedCardLarge: React.FC<FeaturedCardProps> = ({ experience }) => {
  const [isLiked, setIsLiked] = useState(false);
  const nextDate = experience.next_dates?.[0] || "";

  return (
    <div className="group relative bg-white dark:bg-neutral-800 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 h-full min-h-[500px]">
      <div className="relative h-full overflow-hidden">
        <img
          src={experience.cover_image || "https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg"}
          alt={experience.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>

        <div className="absolute top-6 left-6 flex flex-col gap-2">
          <div className="bg-[#f5694b] text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
            <Star className="w-4 h-4 fill-white" />
            <span>FEATURED</span>
          </div>
          <div className="bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full">
            {experience.category}
          </div>
        </div>

        <button
          onClick={() => setIsLiked(!isLiked)}
          className="absolute top-6 right-6 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-lg"
        >
          <Heart className={`w-6 h-6 ${isLiked ? "fill-[#f5694b] text-[#f5694b]" : "text-gray-700"}`} />
        </button>

        <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
          <div className="flex items-center gap-2 text-sm text-white/90 mb-3">
            <MapPin className="w-5 h-5 text-[#f5694b]" />
            <span className="font-medium">{experience.location}</span>
          </div>
          <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3 leading-tight">
            {experience.title}
          </h3>
          <p className="text-base text-white/90 mb-6 line-clamp-2">
            {experience.description}
          </p>
          <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
            <div className="flex items-center gap-4 text-sm text-white/80">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{experience.duration}</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{experience.group_size}</span>
              </div>
            </div>
            {nextDate && (
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                <Calendar className="w-4 h-4 text-white" />
                <span className="text-sm font-semibold text-white">{nextDate}</span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-white/20">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 fill-[#f5694b] text-[#f5694b]" />
              <span className="text-lg font-bold text-white">{experience.rating}</span>
              <span className="text-sm text-white/70">({experience.review_count} reviews)</span>
            </div>
            <Link
              to={`/experiences/${experience.slug}`}
              className="px-6 py-2.5 bg-[#f5694b] hover:bg-[#e2583d] text-white text-sm font-semibold rounded-full transition-all hover:scale-105 shadow-lg"
            >
              View Details
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeaturedCardMedium: React.FC<FeaturedCardProps> = ({ experience }) => {
  const [isLiked, setIsLiked] = useState(false);
  const nextDate = experience.next_dates?.[0] || "";

  return (
    <div className="group relative bg-white dark:bg-neutral-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 h-full min-h-[350px]">
      <div className="relative h-full overflow-hidden">
        <img
          src={experience.cover_image || "https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg"}
          alt={experience.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>

        <div className="absolute top-4 left-4 bg-[#f5694b] text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg">
          {experience.category}
        </div>

        <button
          onClick={() => setIsLiked(!isLiked)}
          className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-lg"
        >
          <Heart className={`w-5 h-5 ${isLiked ? "fill-[#f5694b] text-[#f5694b]" : "text-gray-700"}`} />
        </button>

        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-1 text-sm text-white/90 mb-2">
            <MapPin className="w-4 h-4 text-[#f5694b]" />
            <span>{experience.location}</span>
          </div>
          <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">{experience.title}</h3>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-xs text-white/80">
              <Clock className="w-4 h-4" />
              <span>{experience.duration}</span>
            </div>
            {nextDate && (
              <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-2 py-1 rounded">
                <Calendar className="w-3 h-3 text-white" />
                <span className="text-xs font-semibold text-white">{nextDate}</span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-white/20">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 fill-[#f5694b] text-[#f5694b]" />
              <span className="font-bold text-white">{experience.rating}</span>
            </div>
            <Link
              to={`/experiences/${experience.slug}`}
              className="px-4 py-2 bg-[#f5694b] hover:bg-[#e2583d] text-white text-sm font-semibold rounded-full transition-all hover:scale-105"
            >
              View Details
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeaturedCardSmall: React.FC<FeaturedCardProps> = ({ experience }) => {
  const [isLiked, setIsLiked] = useState(false);

  return (
    <div className="group relative bg-white dark:bg-neutral-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 h-full min-h-[240px]">
      <div className="relative h-full overflow-hidden">
        <img
          src={experience.cover_image || "https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg"}
          alt={experience.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>

        <div className="absolute top-3 left-3 bg-[#f5694b] text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-lg">
          {experience.category}
        </div>

        <button
          onClick={() => setIsLiked(!isLiked)}
          className="absolute top-3 right-3 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-lg"
        >
          <Heart className={`w-4 h-4 ${isLiked ? "fill-[#f5694b] text-[#f5694b]" : "text-gray-700"}`} />
        </button>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-lg font-bold text-white mb-2 line-clamp-2">{experience.title}</h3>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-[#f5694b] text-[#f5694b]" />
              <span className="text-sm font-bold text-white">{experience.rating}</span>
            </div>
            <Link
              to={`/experiences/${experience.slug}`}
              className="px-3 py-1.5 bg-[#f5694b] hover:bg-[#e2583d] text-white text-xs font-semibold rounded-full transition-all hover:scale-105"
            >
              Details
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeaturedCardWide: React.FC<FeaturedCardProps> = ({ experience }) => {
  const [isLiked, setIsLiked] = useState(false);
  const nextDate = experience.next_dates?.[0] || "";

  return (
    <div className="group relative bg-white dark:bg-neutral-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 h-full min-h-[240px]">
      <div className="relative h-full overflow-hidden">
        <img
          src={experience.cover_image || "https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg"}
          alt={experience.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent"></div>

        <div className="absolute top-4 left-4 bg-[#f5694b] text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg">
          {experience.category}
        </div>

        <button
          onClick={() => setIsLiked(!isLiked)}
          className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-lg"
        >
          <Heart className={`w-5 h-5 ${isLiked ? "fill-[#f5694b] text-[#f5694b]" : "text-gray-700"}`} />
        </button>

        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-center gap-2 text-sm text-white/90 mb-2">
            <MapPin className="w-4 h-4 text-[#f5694b]" />
            <span>{experience.location}</span>
          </div>
          <h3 className="text-xl lg:text-2xl font-bold text-white mb-3">{experience.title}</h3>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4 text-sm text-white/80">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{experience.duration}</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{experience.group_size}</span>
              </div>
              {nextDate && (
                <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                  <Calendar className="w-4 h-4" />
                  <span className="font-semibold">{nextDate}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-[#f5694b] text-[#f5694b]" />
                <span className="font-bold text-white">{experience.rating}</span>
              </div>
              <Link
                to={`/experiences/${experience.slug}`}
                className="px-6 py-2.5 bg-[#f5694b] hover:bg-[#e2583d] text-white text-sm font-semibold rounded-full transition-all hover:scale-105 shadow-lg"
              >
                View Details
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// REGULAR EXPERIENCE CARD
// ============================================

const ExperienceCard: React.FC<{ experience: ExperienceSummary }> = ({ experience }) => {
  const [isLiked, setIsLiked] = useState(false);
  const nextDate = experience.next_dates?.[0] || "";

  return (
    <div className="group relative bg-white dark:bg-neutral-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
      <div className="relative h-64 overflow-hidden">
        <img
          src={experience.cover_image || "https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg"}
          alt={experience.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

        <div className="absolute top-4 left-4 bg-[#f5694b] text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg">
          {experience.category}
        </div>

        <button
          onClick={() => setIsLiked(!isLiked)}
          className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-lg"
        >
          <Heart className={`w-5 h-5 ${isLiked ? "fill-[#f5694b] text-[#f5694b]" : "text-gray-700"}`} />
        </button>

        {nextDate && (
          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm text-gray-900 text-sm font-semibold px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#f5694b]" />
            {nextDate}
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 mb-2">
          <MapPin className="w-4 h-4 text-[#f5694b]" />
          <span>{experience.location}</span>
        </div>

        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-[#f5694b] transition-colors">
          {experience.title}
        </h3>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
          {experience.description}
        </p>

        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mb-4 pb-4 border-b border-gray-200 dark:border-neutral-700">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{experience.duration}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span>{experience.group_size}</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 fill-[#f5694b] text-[#f5694b]" />
            <span className="font-semibold text-gray-900 dark:text-white">{experience.rating}</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">({experience.review_count})</span>
          </div>
          <Link
            to={`/experiences/${experience.slug}`}
            className="px-4 py-2 bg-[#f5694b] hover:bg-[#e2583d] text-white text-sm font-semibold rounded-full transition-all hover:scale-105"
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ExperiencesPage;